import moment from 'moment';
import React, { Component } from 'react';
import Toggle from 'react-toggle';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import { retrieveResources } from '@/shared/components/registration/resourceApi';
import YearButtonGroup from '@/shared/components/YearButtonGroup';
import UserSearch from '@/shared/components/input/UserSearch';
import { FaUndo } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';
import CompanySelector from '@/shared/components/input/CompanySelector';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import EnumSelectV2 from '@/shared/components/input/EnumSelectV2';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import messages from '../hrmMessages';

export const defaultFilters = {
  year: moment()
    .utc()
    .year(),
  resourceId: null,
  resourceCategoryId: null,
  userId: null,
  companyId: null,
  employeeType: null,
  withEmployeeLabels: [],
  onlyOpen: false,
};

const currentYear = moment.utc().year();
const yearFrom = currentYear - 2;
const yearUntil = currentYear + 1;

class HrmOverviewFilters extends Component {
  state = {
    resources: [],
  };
  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      year: PropTypes.number.isRequired,
      resourceId: PropTypes.string,
      resourceCategoryId: PropTypes.string,
      userId: PropTypes.string,
      companyId: PropTypes.string,
      employeeType: PropTypes.number,
      withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
      onlyOpen: PropTypes.bool,
    }),
    inDays: PropTypes.bool,
    inDaysChanged: PropTypes.func,
    intl: intlShape.isRequired,
  };
  static defaultProps = {
    filters: defaultFilters,
    inDaysChanged: () => {},
  };

  componentDidMount() {
    const { intl } = this.props;
    retrieveResources(intl, 3).then(data => {
      this.setState({
        resources: data.filter(x => {
          if (x.useCounter) return true;

          return x.isDefault && data.some(y => y.categoryId === x.categoryId && y.useCounter);
        }),
      });
    });
  }
  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };
  resetFilters = () => {
    this.filterChanged(defaultFilters);
  };
  resourceFilterChanged = resource => {
    let resourceId = null;
    let categoryId = null;

    if (resource) {
      if (resource.isDefault) {
        // eslint-disable-next-line prefer-destructuring
        categoryId = resource.categoryId;
      } else {
        resourceId = resource.id;
      }
    }
    this.filterChanged({ resourceId, resourceCategoryId: categoryId });
  };
  render() {
    const { resources } = this.state;
    const { filters, intl, inDaysChanged, inDays } = this.props;

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);
    const showCompanies = hasMultipleCompanies();

    const selectedResource =
      (filters.resourceCategoryId
        ? resources.find(x => x.categoryId === filters.resourceCategoryId && x.isDefault)
        : null) ?? (filters.resourceId ? resources.find(x => x.id === filters.resourceId) : null);

    return (
      <div>
        <div className="row">
          <div className="col-lg-4 col-md-6 margin5B">
            <div className="form-group">
              <UserSearch
                value={filters.userId}
                onChange={u => this.filterChanged({ userId: u?.id })}
                placeholder={intl.formatMessage(messages.Employee)}
              />
            </div>
          </div>

          <div className="col-lg-4 col-md-6 margin5B">
            <div className="form-group">
              <ResourceAutoComplete
                items={resources}
                onChange={this.resourceFilterChanged}
                value={selectedResource}
                placeholder={intl.formatMessage(messages.FilterResource)}
                intl={intl}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 margin5B">
            <div className="form-group">
              <EnumSelectV2
                enumType="employeeType"
                value={filters.employeeType}
                allowNull
                placeholder={intl.formatMessage(messages.EmployeeType)}
                onChange={value => this.filterChanged({ employeeType: value })}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 col-md-6 margin20B">
            <YearButtonGroup
              value={filters.year}
              yearFrom={yearFrom}
              yearUntil={yearUntil}
              onChange={year => this.filterChanged({ year })}
            />
          </div>
          <div className="col-lg-4 col-md-6 margin5B">
            <EmployeeTagMultiSelect
              value={filters.withEmployeeLabels}
              onChange={withEmployeeLabels => this.filterChanged({ withEmployeeLabels })}
              placeholder={intl.formatMessage(messages.WithEmployeeTags)}
            />
          </div>
          {showCompanies && (
            <div className="col-lg-4 col-md-6 margin5B">
              <div className="form-group">
                <CompanySelector
                  onChange={companyId => this.filterChanged({ companyId: companyId || null })}
                  value={filters.companyId}
                  placeholder={intl.formatMessage(messages.OwnCompany)}
                />
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-lg-4 col-md-6 margin5B">
            <label className="react-toggle-wrapper margin5T">
              <Toggle checked={inDays} onChange={e => inDaysChanged(e.target.checked)} />
              <FormattedMessage className="label-text" {...messages.ShowAbsencesInDays} />
            </label>
          </div>
          <div className="col-lg-4 col-md-6 margin5B">
            <label className="react-toggle-wrapper margin5T">
              <Toggle
                checked={filters.onlyOpen}
                onChange={e => this.filterChanged({ onlyOpen: e.target.checked })}
              />
              <FormattedMessage className="label-text" {...messages.ShowOnlyOpenAbsences} />
            </label>
          </div>
        </div>
        {hasFilters && (
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default HrmOverviewFilters;
